import type { ReactElement } from 'react'

import { useState } from 'react'
import { FileText, Download, Eye, Edit3, Check, X, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'

import { FileManager } from '@/components/form/anexar'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { useFetchSimples } from '@/lib/hooks/simples'
import { AnaliseMensal } from '../../state/selectSimples'
import { useUpdateRestituicaoSimples } from '../../services/useUpdateRestituicaoSimples'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'
import { useCurrentUser } from '@/features/auth/state/auth'
import { formatCompetencia, formatMoeda } from '@/utils/format'
import { exportRetificacaoExcelSimples, exportRetificacaoExcelSimplesDetalhado } from '@/utils/exportPlanilhas'
import { getDiagnosticInfo } from '../../utils/simples-utils'

export default function FinancialTable(): ReactElement {
  const { apuracaoSelecionada } = useApuracaoSelecionada()
  const [editingTotalRestituir, setEditingTotalRestituir] = useState<string | null>(null)
  const [fileManagerOpen, setFileManagerOpen] = useState(false)
  const [fileManagerRowId, setFileManagerRowId] = useState<string | null>(null)
  const [fileManagerRowIdApComp, setFileManagerRowIdApComp] = useState<string | null>(null)
  const { data: apuracoes = [] } = useFetchSimples(apuracaoSelecionada?.id ?? '')
  // dentro do FinancialTable
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedAnalises = [...(apuracoes?.analises ?? [])].sort((a, b) => {
    // normaliza separadores
    const cleanA = a.competencia.replace(/[-/]/g, '-')
    const cleanB = b.competencia.replace(/[-/]/g, '-')

    // transforma em Date para ordenar corretamente
    const [anoA, mesA] = cleanA.split('-').map(Number)
    const [anoB, mesB] = cleanB.split('-').map(Number)

    const dateA = new Date(anoA, mesA - 1).getTime()
    const dateB = new Date(anoB, mesB - 1).getTime()

    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
  })


  const [valorEditado, setValorEditado] = useState<number>(0)

  const user = useCurrentUser() // ou pegue do contexto

  const [editedValues, setEditedValues] = useState<{
    [competencia: string]: { valor: number; usuario: string }
  }>({})
  const useUpdateRestituicao = useUpdateRestituicaoSimples()
  const handleSubmit = async (competencia: string, valor: number) => {
    try {
      await useUpdateRestituicao.mutateAsync({
        id_apuracao: apuracaoSelecionada?.id || '',
        competencia,
        valor
      })
      setEditingTotalRestituir(null)
    } catch (error) {
      console.error('Erro ao atualizar valor a restituir:', error)
    }
  }
  console.log(`Analises:`, apuracoes?.analises ?? [])
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-foreground">Planilha de Retificação</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="text-sm bg-primary hover:bg-primary-hover text-white "
              disabled={!apuracoes?.analises?.length}
              onClick={() => {
                exportRetificacaoExcelSimples(apuracoes ?? [])
              }}
            >
              <FileText className="w-4 h-4 mr-2" />
              Exportar Planilha
            </Button>

            {user?.typeUser === 'ADMIN' && (
              <>
                <Button
                  variant="outline"
                  className="text-sm bg-primary hover:bg-primary-hover text-white"
                  disabled={!apuracoes?.analises?.length}
                  onClick={() => {
                    exportRetificacaoExcelSimplesDetalhado(apuracoes ?? [])
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Exportar Análise
                </Button>
                <Button variant="outline" className="text-sm bg-primary hover:bg-primary-hover text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Notas
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-background-secondary z-10 relative">
            <tr>
              <th
                className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary cursor-pointer select-none"
                onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
              >
                <div className="flex items-center gap-1">
                  Competência
                  {sortOrder === 'asc' ? '▲' : '▼'}
                </div>
              </th>

              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Receita Bruta
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Receita s/ ST/MONO
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Receita c/ ST/MONO
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Retificado ST
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Receita Monofásico
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Total Apurado
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                <div className="flex items-center gap-1">
                  Total a Restituir
                  <Edit3 className="w-3 h-3" />
                </div>
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Diagnóstico
              </th>
              <th className="sticky px-2 py-2 text-left text-xs font-medium text-foreground uppercase tracking-wider bg-background-secondary">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 z-0 relative">
            {sortedAnalises?.length === 0 && (
              <tr>
                <td colSpan={11} className="py-12 text-center text-foreground">
                  <div className="text-4xl mb-4">📊</div>
                  <p className="text-lg">Nenhuma análise encontrada</p>
                  <p className="text-sm">Importe os arquivos para iniciar a análise</p>
                </td>
              </tr>
            )}
            {sortedAnalises?.map((row: AnaliseMensal, index: number) => {
              const diagnosticInfo = getDiagnosticInfo(row.diagnostico)
              const isEditingTotal = editingTotalRestituir === row.competencia
              const valorTemporario = editedValues[row.competencia]
              const valorMostrar = isEditingTotal ? valorEditado : (valorTemporario?.valor ?? row.total_a_restituir)
              return (
                <tr
                  key={formatCompetencia(row.competencia)}
                  className={`${index % 2 === 0 ? 'bg-background' : 'bg-background-secondary'} hover:bg-gray-100`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatCompetencia(row.competencia)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatMoeda(row.rb_auferida)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatMoeda(row.sem_subs_rb)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatMoeda(row.com_subs_rb)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatMoeda(row.sugerido_st)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                    {formatMoeda(row.sugerido_mono_st)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-foreground">
                    {formatMoeda(row.total)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isEditingTotal ? (
                      <div className="flex items-center gap-2">
                        <MaskInputDin
                          defaultValue={row.total_a_restituir}
                          value={valorEditado}
                          onChange={setValorEditado}
                          className="w-24"
                          placeholder="0,00"
                        />
                        <Button
                          size="sm"
                          onClick={async () => {
                            await handleSubmit(row.competencia, valorEditado)
                            setEditedValues(prev => ({
                              ...prev,
                              [row.competencia]: {
                                valor: valorEditado,
                                usuario: user?.name ?? 'Desconhecido'
                              }
                            }))
                          }}
                        >
                          <Check className="w-3 h-3" />
                        </Button>

                        <Button size="sm" variant="outline" onClick={() => setEditingTotalRestituir(null)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer flex flex-col items-start gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          setEditingTotalRestituir(row.competencia)
                          setValorEditado(Number(row.total_a_restituir))
                        }}
                      >
                        <span className="text-sm font-medium text-foreground">{formatMoeda(valorMostrar)}</span>
                        {valorTemporario?.usuario && (
                          <span className="text-xs text-foreground">por {valorTemporario.usuario}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="group relative">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${diagnosticInfo.color}`}
                      >
                        {diagnosticInfo.label}
                      </span>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-background text-black text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap ">
                        {diagnosticInfo.description}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-backbg-background"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setFileManagerRowId(row.id)
                        setFileManagerRowIdApComp(row.id_apuracao_competencia)
                        setFileManagerOpen(true)
                      }}
                    >
                      <Paperclip className="w-3 h-3 mr-2" />
                      Anexar
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {fileManagerOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
            <div className="rounded-lg shadow-lg w-full max-w-md bg-white relative">
              <FileManager
                id={fileManagerRowId || ''}
                id_apuracao_competencia={fileManagerRowIdApComp || ''}
                type="credit"
                id_client={apuracaoSelecionada?.id_client || ''}
                id_apuracao={apuracaoSelecionada?.id || ''}
                id_product={apuracaoSelecionada?.id_product || ''}
                onClose={() => setFileManagerOpen(false)} // <<<<<< AQUI
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
